import { LegalityTypes } from 'constants/LegalityTypes';
import { ELegalVisibility } from 'constants/LegalVisibility';
import { LETypes } from 'constants/LegalEntityTypes';
import { EPermission } from 'models/permissions';
import { EState } from './transactionUtils';

interface IFirearmValidation {
  stateUniqueId?: string;
  legalityUniqueId?: string;
  owner?: {
    uniqueId?: string;
    type?: string;
    visibilityUniqueId?: string;
    prefixGroupUniqueId?: string;
  };
  keeper?: {
    uniqueId?: string;
    type?: string;
    visibilityUniqueId?: string;
    prefixGroupUniqueId?: string;
  };
}

interface IAccountContext {
  uniqueId?: string;
  prefixGroupIds?: string[];
  assignedLegalIds?: string[];
  licenceAuthorityIds?: string[];
  permissions?: EPermission[];
}

interface ILegalEntity {
  uniqueId?: string;
  visibilityUniqueId?: string;
  prefixGroupUniqueId?: string;
  type?: string;
  licenceAuthorityId?: string;
}

const hasViewPermissionForLE = (
  le: ILegalEntity | undefined,
  account: IAccountContext | undefined,
  hasViewAllFirearmsPermission: boolean,
  hasViewRestrictedLEPermission: boolean,
  hasViewGlobalLEPermission: boolean,
): boolean => {
  if (!le || !account) return false;

  if (hasViewAllFirearmsPermission) {
    return true;
  }

  const isNonPublic = le.visibilityUniqueId === ELegalVisibility.NON_PUBLIC;
  if (isNonPublic && !hasViewRestrictedLEPermission) {
    return false;
  }

  const isPublic = le.visibilityUniqueId === ELegalVisibility.PUBLIC;
  const isAssignedToUser = account.assignedLegalIds?.includes(le.uniqueId || '');
  if (isPublic && !isAssignedToUser && !hasViewGlobalLEPermission) {
    return false;
  }

  if (
    le.licenceAuthorityId &&
    account.licenceAuthorityIds &&
    !account.licenceAuthorityIds.includes(le.licenceAuthorityId)
  ) {
    return false;
  }

  if (le.type === LETypes.individual) {
    if (
      le.prefixGroupUniqueId &&
      account.prefixGroupIds &&
      !account.prefixGroupIds.includes(le.prefixGroupUniqueId)
    ) {
      return false;
    }
  }

  return true;
};

export const isIllegalFirearm = (
  firearm: IFirearmValidation,
  expectedOwnerUniqueId?: string,
): boolean => {
  const isInProhibitedState =
    firearm.stateUniqueId === EState.DESTROYED ||
    firearm.stateUniqueId === EState.DISABLED ||
    firearm.stateUniqueId === EState.LOST ||
    firearm.stateUniqueId === EState.EXPORTED;

  const isIllegal = firearm.legalityUniqueId === LegalityTypes.ILLEGAL;

  const ownerMismatch =
    firearm.owner?.uniqueId !== expectedOwnerUniqueId &&
    firearm.keeper?.uniqueId !== expectedOwnerUniqueId;

  return isInProhibitedState || isIllegal || ownerMismatch;
};

export const shouldRemoveFirearmFromLicence = (
  firearm: IFirearmValidation,
  licenceOwnerUniqueId?: string,
  account?: IAccountContext,
  hasViewAllFirearmsPermission: boolean = false,
  hasViewRestrictedLEPermission: boolean = false,
  hasViewGlobalLEPermission: boolean = false,
): boolean => {
  const isInProhibitedState =
    firearm.stateUniqueId === EState.DESTROYED ||
    firearm.stateUniqueId === EState.DISABLED ||
    firearm.stateUniqueId === EState.LOST ||
    firearm.stateUniqueId === EState.EXPORTED;

  if (isInProhibitedState) {
    return true;
  }

  const ownerIsLicenceHolder = firearm.owner?.uniqueId === licenceOwnerUniqueId;
  const keeperIsLicenceHolder = firearm.keeper?.uniqueId === licenceOwnerUniqueId;

  if (!ownerIsLicenceHolder && !keeperIsLicenceHolder) {
    return true;
  }

  if (!account || ownerIsLicenceHolder || keeperIsLicenceHolder) {
    return false;
  }

  const ownerLE: ILegalEntity = {
    uniqueId: firearm.owner?.uniqueId,
    visibilityUniqueId: firearm.owner?.visibilityUniqueId,
    prefixGroupUniqueId: firearm.owner?.prefixGroupUniqueId,
    type: firearm.owner?.type,
  };

  const keeperLE: ILegalEntity = {
    uniqueId: firearm.keeper?.uniqueId,
    visibilityUniqueId: firearm.keeper?.visibilityUniqueId,
    prefixGroupUniqueId: firearm.keeper?.prefixGroupUniqueId,
    type: firearm.keeper?.type,
  };

  const hasOwnerPermission = hasViewPermissionForLE(
    ownerLE,
    account,
    hasViewAllFirearmsPermission,
    hasViewRestrictedLEPermission,
    hasViewGlobalLEPermission,
  );

  const hasKeeperPermission = hasViewPermissionForLE(
    keeperLE,
    account,
    hasViewAllFirearmsPermission,
    hasViewRestrictedLEPermission,
    hasViewGlobalLEPermission,
  );

  if (!hasOwnerPermission && !hasKeeperPermission) {
    return true;
  }

  return false;
};

export const hasFirearmsToRemove = (
  firearms: any[],
  licenceOwnerUniqueId: string | undefined,
  account: IAccountContext | undefined,
  hasViewAllFirearmsPermission: boolean,
  hasViewAllLEPermission: boolean,
): boolean => {
  if (!firearms || !account) return false;

  return firearms.some((firearm) =>
    shouldRemoveFirearmFromLicence(
      {
        stateUniqueId: firearm.stateUniqueId,
        legalityUniqueId: firearm.legalityUniqueId,
        owner: {
          uniqueId: firearm.owner?.uniqueId,
          type: firearm.owner?.type,
          visibilityUniqueId: firearm.owner?.visibilityUniqueId,
          prefixGroupUniqueId: firearm.owner?.prefixGroupUniqueId,
        },
        keeper: firearm.keeper?.uniqueId
          ? {
            uniqueId: firearm.keeper?.uniqueId,
            type: firearm.keeper?.type,
            visibilityUniqueId: firearm.keeper?.visibilityUniqueId,
            prefixGroupUniqueId: firearm.keeper?.prefixGroupUniqueId,
          }
          : undefined,
      },
      licenceOwnerUniqueId,
      account,
      hasViewAllFirearmsPermission,
      hasViewAllLEPermission,
    ),
  );
};
